/**
 * AI CONTEXT
 * Purpose: computes and displays next project timing from current session/project fraction.
 * Data access: direct Firebase reads for next session/project docs.
 * Used by: TeacherAttendance.
 * Importance: gives teacher forward visibility into upcoming project handoff.
 * Refactor: replace direct reads with cached/subscription path to avoid repeated network calls.
 */
import dayjs from "dayjs"
import { useEffect, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { generateSessionId, getSessionInfoFromId } from "#utils/classHelpers"
import { getNow, STANDARD_DATE_FORMAT } from "#utils/dateHelpers"

import { useTeacherViewContext } from "./TeacherViewContextProvider"

const useNextProjectInfo = () => {
  const { selectedSession } = useTeacherViewContext()
  const currentProject = selectedSession?.project
  const classSessionIds = selectedSession?.class?.sessionIds

  const [nextProject, setNextProject] = useState<
    | {
        projectId: string
        projectName: string
        startDate: string
        daysUntilStart: number
      }
    | undefined
  >()

  useEffect(() => {
    const findNextProject = async () => {
      if (!selectedSession?.id || !currentProject || !classSessionIds?.length)
        return

      const [classId, currentSessionIndex] = getSessionInfoFromId(
        selectedSession.id
      )

      // Calculate how many days the current project has been worked on
      const currentProjectDaysCompleted =
        selectedSession.session.projectFraction || 0
      const currentProjectDaysToComplete =
        parseInt(currentProject.daysToComplete) || 0

      // Calculate remaining days for current project
      const remainingDays = Math.max(
        0,
        currentProjectDaysToComplete - currentProjectDaysCompleted
      )

      const nextProjectSessionIndex = currentSessionIndex + remainingDays + 1

      if (nextProjectSessionIndex < classSessionIds.length) {
        const nextSessionId = generateSessionId(
          classId,
          nextProjectSessionIndex
        )
        const nextSession =
          await Firebase.getInstance().getDocumentById<ClassSessionConstructor>(
            DOC_TYPES.CLASS_SESSIONS,
            nextSessionId
          )

        if (nextSession) {
          const nextProjectFromDb =
            await Firebase.getInstance().getDocumentById<ProjectConstructor>(
              DOC_TYPES.PROJECTS,
              nextSession.projectId
            )

          if (nextProjectFromDb) {
            // Calculate days until this project starts
            const now = getNow()
            const projectStartDate = dayjs(nextSession.eventStart)
            const daysUntilStart = projectStartDate.diff(now, "day")

            setNextProject({
              projectId: nextProjectFromDb.id,
              projectName: nextProjectFromDb.name,
              startDate: dayjs(nextSession.eventStart).format(
                STANDARD_DATE_FORMAT
              ),
              daysUntilStart: Math.max(0, daysUntilStart),
            })
            return
          }
        }
      }
      setNextProject(undefined)
    }

    findNextProject()
  }, [selectedSession, currentProject, classSessionIds])

  return nextProject
}

const NextProjectInfo = () => {
  const nextProject = useNextProjectInfo()

  if (!nextProject)
    return (
      <AnimateInFlex
        animationKey="no-next-project"
        animation={ANIMATION_BY_KEY.leftSm}
        style={{ fontSize: 14, padding: "0px 40px" }}
      >
        No next project found
      </AnimateInFlex>
    )

  const { projectName, startDate, daysUntilStart } = nextProject

  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      animationKey={`next-project-${nextProject.projectId}`}
      style={{
        padding: "0px 40px 10px",
      }}
      col
    >
      <Flex gap={10} style={{ fontSize: 14 }}>
        Next project: {projectName} on {startDate}
        {daysUntilStart > 0 && (
          <span style={{ color: "#666" }}>
            {" "}
            (in {daysUntilStart} day{daysUntilStart !== 1 ? "s" : ""})
          </span>
        )}
      </Flex>
    </AnimateInFlex>
  )
}

export default NextProjectInfo
